import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@store/index';

/**
 * Planner screen shared interaction state (feature 107).
 *
 * Holds business-critical/shared UI state that previously lived as complex component-local
 * state in `PlannerScreen` (search text, multi-select, sort, filter-panel visibility) so it
 * is updated through reducers and read from the store (FR-016). Selections are stored as a
 * serializable `string[]` rather than a `Set` to keep the store serializable.
 */

export type PlannerSortDirection = 'ascending' | 'descending';

export interface PlannerSortState {
  sortColumn: string;
  sortDirection: PlannerSortDirection;
}

export interface PlannerUiState {
  searchQuery: string;
  selectedEventIds: string[];
  filterPanelOpen: boolean;
  sortState: PlannerSortState | null;
}

const initialState: PlannerUiState = {
  searchQuery: '',
  selectedEventIds: [],
  filterPanelOpen: false,
  sortState: null,
};

export const plannerUiSlice = createSlice({
  name: 'plannerUi',
  initialState,
  reducers: {
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setSelectedEventIds: (state, action: PayloadAction<string[]>) => {
      state.selectedEventIds = Array.from(new Set(action.payload));
    },
    clearSelectedEventIds: (state) => {
      state.selectedEventIds = [];
    },
    setFilterPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.filterPanelOpen = action.payload;
    },
    setSortState: (state, action: PayloadAction<PlannerSortState | null>) => {
      state.sortState = action.payload;
    },
    resetPlannerUi: () => initialState,
  },
});

export const {
  setSearchQuery,
  setSelectedEventIds,
  clearSelectedEventIds,
  setFilterPanelOpen,
  setSortState,
  resetPlannerUi,
} = plannerUiSlice.actions;

export const plannerUiReducer = plannerUiSlice.reducer;

export const selectPlannerSearchQuery = (state: RootState): string => state.plannerUi.searchQuery;
export const selectPlannerSelectedEventIds = (state: RootState): string[] =>
  state.plannerUi.selectedEventIds;
export const selectPlannerFilterPanelOpen = (state: RootState): boolean =>
  state.plannerUi.filterPanelOpen;
export const selectPlannerSortState = (state: RootState): PlannerSortState | null =>
  state.plannerUi.sortState;
